/**
 * Student Upload Help Component
 *
 * Collapsible accordion showing Studio Pro export instructions
 * and template download guidance.
 */

import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const StudentUploadHelp: React.FC = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ mb: 3 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" gap={1}>
          <InfoIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="medium">
            How to Upload Your Student Roster
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {/* Studio Pro Instructions */}
          <Typography variant="h6" gutterBottom>
            For Studio Pro Users
          </Typography>
          <Typography variant="body2" paragraph>
            Follow these steps to export your student data from Dance Studio
            Pro:
          </Typography>
          <Box component="ol" sx={{ pl: 2, mb: 3 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Click on the <strong>&quot;More&quot;</strong> menu in Studio Pro
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Click on <strong>&quot;Reports&quot;</strong>
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              On the <strong>&quot;Students&quot;</strong> tab, click{' '}
              <strong>&quot;Active Students (Sorted by Last Name)&quot;</strong>
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Scroll to the end of the report at the bottom of the page
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Click on the <strong>&quot;Download To Excel&quot;</strong> link
            </Typography>
            <Typography component="li" variant="body2">
              Upload the downloaded Excel file using the{' '}
              <strong>&quot;Upload Excel&quot;</strong> button above
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Important:
            </Typography>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>
                  Use{' '}
                  <strong>
                    &quot;Active Students (Sorted by Last Name)&quot;
                  </strong>{' '}
                  - this exports as an Excel file
                </li>
                <li>
                  Do <strong>NOT</strong> use &quot;Active Students (Sorted by
                  First Name)&quot; - this exports as a CSV file and will not
                  work
                </li>
              </ul>
            </Typography>
          </Alert>

          {/* Non-Studio Pro Instructions */}
          <Typography variant="h6" gutterBottom>
            For Non-Studio Pro Users
          </Typography>
          <Typography variant="body2" paragraph>
            If you don&apos;t use Dance Studio Pro:
          </Typography>
          <Box component="ol" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Click the <strong>&quot;Download Template&quot;</strong> button
              above
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Open the template in Excel or a compatible spreadsheet program
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              Fill in your student information
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Do not alter the column headers or structure</strong>
            </Typography>
            <Typography component="li" variant="body2">
              Save and upload the completed file using the{' '}
              <strong>&quot;Upload Excel&quot;</strong> button above
            </Typography>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default StudentUploadHelp;
